// src/pages/SchoolPrograms.jsx
//
// School-admin view: browse GEFEN programs open to join, join one, then
// see each joined program as a card with budget/coupon-utilization
// stats and buttons to purchase budget / issue coupons to students.
//
// Visual language matches OnlineClass.jsx — the page shell (bg-gray-100,
// max-w-8xl container), the pill-style section toggle, the white
// rounded-2xl/border-gray-200/shadow-sm "filter bar" card around the
// search box, the dashed-border empty state with a centered icon tile,
// and the small rounded-full status/filter badges — so this page reads
// as part of the same product rather than a bolt-on.
import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useSchoolPrograms } from '../contexts/SchoolProgramsContext';
import IssueCouponsModal from '../components/modals/IssueCouponsModal';
import PurchaseBudgetModal from '../components/modals/PurchaseBudgetModal';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';
import SchoolProgramsHeader from '../components/ui/SchoolProgramsHeader';

const View = { MINE: 'MINE', CATALOG: 'CATALOG' };

const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

const cls = (...parts) => parts.filter(Boolean).join(' ');

// Same KPI-card shape and design as AdminConfirmDocuments' status
// counters: bg-gradient-to-r card fill, a solid icon tile, and title/
// value text colored to match the category (not plain gray) — reused
// here for the program-wide budget totals instead of the plain
// StatTile grid.
const KpiCard = ({ title, value, hint, icon, gradient, iconBgClass, titleClass, valueClass }) => (
  <div className={cls('p-3 sm:p-4 rounded-xl border', gradient)}>
    <div className="flex items-center gap-2 sm:gap-3">
      <div
        className={cls(
          'w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0',
          iconBgClass || 'bg-gradient-to-br from-gray-600 to-gray-800'
        )}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={cls('text-xs sm:text-sm', titleClass || 'text-gray-700')}>{title}</p>
        <p className={cls('text-xl sm:text-2xl font-bold', valueClass || 'text-gray-900')}>{value}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
    </div>
  </div>
);

const IconBudget = (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="13" rx="2" />
    <path d="M2 10h20" />
    <circle cx="17" cy="14.5" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

const IconIssued = (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 9a2 2 0 100 4v3a2 2 0 002 2h12a2 2 0 002-2v-3a2 2 0 100-4V6a2 2 0 00-2-2H6a2 2 0 00-2 2v3z" />
  </svg>
);

const IconRedeemed = (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Distinct from IconRedeemed: that checkmark is "scanned into a
// wallet"; this open-book icon marks the further stage — the wallet
// balance actually spent on a lesson.
const IconUsedForLessons = (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6.5C4 5.67 4.67 5 5.5 5H11v14H5.5A1.5 1.5 0 014 17.5v-11z" />
    <path d="M20 6.5c0-.83-.67-1.5-1.5-1.5H13v14h5.5a1.5 1.5 0 001.5-1.5v-11z" />
  </svg>
);

// Matches a program against a free-text query across the fields a
// school would plausibly search by: name, GEFEN program number,
// description/target audience (the closest thing to a "subject"),
// and supplier name.
const matchesQuery = (program, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    program.name ?? program.programName,
    program.gefenProgramNumber,
    program.description ?? program.programDescription,
    program.targetAudience,
    program.supplierName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

// Search box for filtering the program list — a clear (×) button
// appears once there's text, and the whole thing gets a subtle
// indigo ring on focus (via focus-within on the wrapper, so the icon
// tints along with the input rather than just the border).
// Pill search box — a gradient "glow" ring appears around the whole
// pill on focus (via a gradient-filled wrapper that's transparent
// until then), and the search icon sits in its own circle that fills
// with the same gradient once focused, instead of a plain bordered
// input.
const SearchBox = ({ value, onChange, placeholder }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div className="relative flex-1 min-w-[240px]">
      <div
        className={`rounded-2xl p-[1.5px] transition-all duration-300 ${
          focused
            ? 'bg-gradient-to-l from-indigo-500 via-purple-500 to-indigo-500 shadow-lg shadow-indigo-100'
            : 'bg-transparent'
        }`}
      >
        <div
          className={`flex items-center h-12 rounded-2xl bg-gray-50 pl-2 pr-1.5 transition-colors ${
            focused ? 'bg-white' : 'border border-gray-200'
          }`}
        >
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              focused ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white' : 'bg-white text-gray-400 border border-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
            </svg>
          </div>

          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            className="flex-1 h-full bg-transparent border-none outline-none text-sm px-2.5 placeholder:text-gray-400"
          />

          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              aria-label="ניקוי חיפוש"
              className="flex-shrink-0 w-7 h-7 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ pct }) => (
  <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
    <div
      className="h-full rounded-full bg-gradient-to-l from-indigo-500 to-purple-600 transition-all"
      style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
    />
  </div>
);

const EmptyState = ({ icon, title, body }) => (
  <div className="flex flex-col items-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 justify-center py-16 text-center">
    <div className="mb-4 w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
      {icon}
    </div>
    <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
    {body && <p className="text-sm text-gray-500 max-w-sm">{body}</p>}
  </div>
);

const IconTicketStack = (
  <svg className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 9a2 2 0 100 4v3a2 2 0 002 2h12a2 2 0 002-2v-3a2 2 0 100-4V6a2 2 0 00-2-2H6a2 2 0 00-2 2v3z" />
  </svg>
);

const IconCatalog = (
  <svg className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const IconInfo = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconPhone = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.05 11.05 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const IconMail = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Program-card status identity — school_programs.status is
// ACTIVE/PAUSED/CLOSED. One gradient + label per state so a whole grid
// of cards reads by color before anyone reads the badge text (same
// language as the supplier-side program cards).
const SCHOOL_STATUS_LABELS = { ACTIVE: 'פעיל', PAUSED: 'מושהה', CLOSED: 'סגור' };
const SCHOOL_STATUS_GRADIENT = {
  ACTIVE: 'from-emerald-400 via-teal-500 to-emerald-600',
  PAUSED: 'from-amber-400 via-orange-500 to-amber-600',
  CLOSED: 'from-gray-400 via-gray-500 to-slate-600',
};
const SCHOOL_STATUS_DOT = { ACTIVE: 'bg-emerald-500', PAUSED: 'bg-amber-500', CLOSED: 'bg-gray-400' };
// Softer, pastel variant of the same status hue — used for a light
// "glass" banner instead of a bold saturated one, plus a matching
// blurred blob accent and status-pill tint.
const SCHOOL_STATUS_SOFT_BG = {
  ACTIVE: 'from-emerald-50 via-white to-teal-50',
  PAUSED: 'from-amber-50 via-white to-orange-50',
  CLOSED: 'from-gray-50 via-white to-slate-50',
};
const SCHOOL_STATUS_BLOB = { ACTIVE: 'bg-emerald-300/40', PAUSED: 'bg-amber-300/40', CLOSED: 'bg-gray-300/40' };
const SCHOOL_STATUS_PILL = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  CLOSED: 'bg-gray-200 text-gray-600',
};

const IconCoupon = (
  <svg className="w-5.5 h-5.5 text-white" width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 9a2 2 0 100 4v3a2 2 0 002 2h12a2 2 0 002-2v-3a2 2 0 100-4V6a2 2 0 00-2-2H6a2 2 0 00-2 2v3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 4v16" strokeDasharray="2 2" />
  </svg>
);

// Supplier contact details — collapsed by default so the card stays
// scannable, expanded on demand. The program description used to live
// inside this same toggle, but it's now always visible directly on
// the card (see SchoolProgramCard/CatalogCard), so this only covers
// contact info. Shared between the joined-program card and the
// catalog card.
const SupplierDetails = ({ program }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = program.supplierEmail || program.supplierPhone;
  if (!hasDetails) return null;

  return (
    <div className="border-t border-gray-100 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700"
      >
        {IconInfo}
        {open ? 'הסתרת פרטי ספק' : 'פרטי ספק'}
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="mt-2 space-y-2 text-xs text-gray-600 bg-gray-50 rounded-xl p-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5 text-gray-500">
              <span className="text-gray-400">איש קשר:</span>
              <span className="font-medium text-gray-700">{program.supplierName || '—'}</span>
            </div>
            {program.supplierPhone && (
              <a
                href={`tel:${program.supplierPhone}`}
                className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 w-fit"
                dir="ltr"
              >
                {IconPhone}
                <span dir="ltr">{program.supplierPhone}</span>
              </a>
            )}
            {program.supplierEmail && (
              <a
                href={`mailto:${program.supplierEmail}`}
                className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 w-fit"
                dir="ltr"
              >
                {IconMail}
                <span dir="ltr">{program.supplierEmail}</span>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Program card — a coupon's value actually passes through three
// stages, and each gets its own stat here instead of being collapsed
// into one vague "אחוז מימוש":
//   1) issued, still uncollected  -> couponsPending (count)
//   2) scanned — the student scanned the coupon's QR and its value
//      landed in their wallet for this program -> couponsScannedAmount
//   3) used for lessons — that wallet balance was actually spent on a
//      lesson purchase -> couponsUsedForLessonsAmount
// Stage 2 is a strict superset of stage 3 (money can sit scanned-but-
// unspent in a wallet), so showing both — not just the final one — is
// the point: a school can see coupons piling up in student wallets
// without being spent.
const SchoolProgramCard = ({ program, onIssueCoupons, onPurchaseBudget }) => {
  const usedPct = program.allocatedBudget > 0
    ? (program.couponsIssuedAmount / program.allocatedBudget) * 100
    : 0;
  const statusLabel = SCHOOL_STATUS_LABELS[program.status] || program.status;

  return (
    <div className="group relative bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-0.5 hover:border-gray-300 transition-all duration-300 overflow-hidden">
      {/* Soft "glass" header in the page's own indigo/purple brand wash.
          Fixed height (rather than growing with content) + flex-col
          justify-between, so every card in a grid lines up on the same
          banner height whether or not a program has a GEFEN number,
          and regardless of name/supplier length (both are truncated
          to one line instead of wrapping and stretching the banner). */}
      <div className="relative h-28 flex flex-col justify-between rounded-t-3xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 overflow-hidden">
        <div className="absolute -right-10 -top-12 w-40 h-40 rounded-full blur-3xl bg-indigo-300/30" />

        {/* Status as a diagonal corner ribbon — same treatment as the
            "זמין להצטרפות" ribbon on CatalogCard — colored by status
            (SCHOOL_STATUS_PILL) so ACTIVE/PAUSED/CLOSED stay
            distinguishable at a glance. */}
        <span className="absolute top-7 -left-11 w-40 rotate-[-45deg] bg-white/90 text-indigo-700 text-[10px] font-bold text-center py-1 shadow-sm">
          {statusLabel}
        </span>

        <div className="relative flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
              {IconCoupon}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 text-[15px] leading-snug truncate">{program.programName}</h3>
              <p className="text-xs text-gray-500 truncate">{program.supplierName || '—'}</p>
            </div>
          </div>
        </div>

        <span
          className={`relative inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-md bg-white/70 text-gray-500 font-mono w-fit ${
            program.gefenProgramNumber ? '' : 'invisible'
          }`}
        >
          מס' תוכנית: {program.gefenProgramNumber || '—'}
        </span>
      </div>

      <div className="p-4 space-y-4">

        <div className="relative">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>ניצול תקציב</span>
            <span className="font-semibold text-gray-700">{Math.round(usedPct)}%</span>
          </div>
          <ProgressBar pct={usedPct} />
          <div className="text-[11px] text-gray-500 mt-1">
            {formatMoney(program.couponsIssuedAmount)} מתוך {formatMoney(program.allocatedBudget)}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-center">
          <div className="rounded-xl bg-gradient-to-b from-indigo-50 to-indigo-100/60 px-2 py-3 transition-transform group-hover:scale-[1.02]">
            <p className="text-sm sm:text-base font-bold text-indigo-700">{formatMoney(program.remainingBudget)}</p>
            <p className="text-[11px] sm:text-xs text-indigo-600/70 mt-1">תקציב פנוי</p>
          </div>
          <div className="rounded-xl bg-gradient-to-b from-gray-50 to-gray-100/60 px-2 py-3 transition-transform group-hover:scale-[1.02]">
            <p className="text-sm sm:text-base font-bold text-gray-600">{program.couponsPending}</p>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-1">טרם נסרקו</p>
          </div>
          <div className="rounded-xl bg-gradient-to-b from-amber-50 to-amber-100/60 px-2 py-3 transition-transform group-hover:scale-[1.02]">
            <p className="text-sm sm:text-base font-bold text-amber-700">{formatMoney(program.couponsScannedAmount)}</p>
            <p className="text-[11px] sm:text-xs text-amber-700/70 mt-1">נסרקו ({program.couponsScannedCount})</p>
          </div>
          <div className="rounded-xl bg-gradient-to-b from-emerald-50 to-emerald-100/60 px-2 py-3 transition-transform group-hover:scale-[1.02]">
            <p className="text-sm sm:text-base font-bold text-emerald-700">{formatMoney(program.couponsUsedForLessonsAmount)}</p>
            <p className="text-[11px] sm:text-xs text-emerald-700/70 mt-1">נוצלו לרכישה</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div>סה״כ קופונים שהונפקו: <span className="font-semibold text-gray-700">{program.couponsTotal}</span></div>
          <div>תלמידים רשומים: <span className="font-semibold text-gray-700">{program.studentsEnrolled}</span></div>
        </div>

        <SupplierDetails program={program} />

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => onPurchaseBudget(program)}
            className="flex-1 rounded-lg border border-indigo-300 text-indigo-700 text-sm font-medium py-2 hover:bg-indigo-50 hover:border-indigo-400 transition-colors"
          >
            רכישת תקציב
          </button>
          <button
            type="button"
            onClick={() => onIssueCoupons(program)}
            className="flex-1 rounded-lg bg-gradient-to-l from-indigo-600 to-purple-600 text-white text-sm font-medium py-2 shadow-sm hover:shadow-md hover:brightness-105 transition-all"
          >
            הנפקת קופונים
          </button>
        </div>
      </div>
    </div>
  );
};

const IconUsers = (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" />
  </svg>
);

const IconArrowLeft = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l-7 7 7 7" />
  </svg>
);

// Catalog card — same layered "glass banner + ribbon + boxed
// description + icon-button contact + gradient CTA" structure as
// before, but recolored to the page's own brand palette (indigo →
// purple, as in the header icon, the "X זמינות" badge and the KPI
// cards) instead of a green that doesn't appear anywhere else on this
// page. Status-style green stays reserved for real ACTIVE programs on
// SchoolProgramCard; a catalog listing isn't a "status," so it wears
// the page's own accent instead of borrowing that meaning.
const CatalogCard = ({ item, onJoin, joining }) => {
  const [expanded, setExpanded] = useState(false);
  const description = item.description || 'לא צוין תיאור לתוכנית זו.';
  // Only bother offering "הרחבה" when the text is actually long enough
  // that a 3-line clamp would plausibly cut it off.
  const isLongDescription = description.length > 140;

  return (
    <div className="group relative bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
      {/* Soft glass banner in the page's own indigo/purple brand wash. */}
      <div className="relative h-28 flex flex-col justify-between rounded-t-3xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 overflow-hidden">
        <div className="absolute -right-10 -top-12 w-40 h-40 rounded-full blur-3xl bg-indigo-300/30" />
        <svg className="absolute -left-4 -bottom-6 w-24 h-24 text-indigo-900/5 rotate-[18deg]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M4 9a2 2 0 100 4v3a2 2 0 002 2h12a2 2 0 002-2v-3a2 2 0 100-4V6a2 2 0 00-2-2H6a2 2 0 00-2 2v3z" />
        </svg>
        <span className="absolute top-7 -left-11 w-40 rotate-[-45deg] bg-white/90 text-indigo-700 text-[10px] font-bold text-center py-1 shadow-sm">
          זמין להצטרפות
        </span>

        <div className="relative flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
              {IconCoupon}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 text-[15px] leading-snug truncate">{item.name}</h3>
              <p className="text-xs text-gray-500 truncate">{item.supplierName || '—'}</p>
            </div>
          </div>
        </div>

        <span
          className={`relative inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-md bg-white/70 text-gray-500 font-mono w-fit ${
            item.gefenProgramNumber ? '' : 'invisible'
          }`}
        >
          מס' תוכנית: {item.gefenProgramNumber || '—'}
        </span>
      </div>

      <div className="p-4 space-y-3.5">
        {item.targetAudience && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700 bg-indigo-50 rounded-full px-2.5 py-1 w-fit">
            {IconUsers}
            {item.targetAudience}
          </span>
        )}

        {/* Description — the main decision-making text, so it's never
            silently truncated: clamped to 3 lines only until the school
            explicitly asks to see the rest. */}
        <div className="relative rounded-xl bg-gray-50/80 border border-gray-100 p-2.5">
          <p className={cls('text-xs text-gray-600 leading-relaxed', !expanded && 'line-clamp-3')}>
            {description}
          </p>
          {isLongDescription && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 mt-1.5"
            >
              {expanded ? 'הצגה מקוצרת' : 'קריאת התיאור המלא ←'}
            </button>
          )}
        </div>

        {/* Supplier / contact — round icon "buttons" instead of plain
            text links, echoing the emblem above so the card reads as
            one consistent design. */}
        {(item.supplierName || item.supplierPhone || item.supplierEmail) && (
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-1">
              איש קשר: <span className="text-gray-600 normal-case font-medium">{item.supplierName || '—'}</span>
            </p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {item.supplierPhone && (
                <a
                  href={`tel:${item.supplierPhone}`}
                  title={item.supplierPhone}
                  className="w-7 h-7 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center justify-center transition-colors"
                >
                  {IconPhone}
                </a>
              )}
              {item.supplierEmail && (
                <a
                  href={`mailto:${item.supplierEmail}`}
                  title={item.supplierEmail}
                  className="w-7 h-7 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center justify-center transition-colors"
                >
                  {IconMail}
                </a>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          disabled={joining}
          onClick={() => onJoin(item.id)}
          className="group/btn w-full flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-l from-indigo-600 to-purple-600 text-white text-sm font-semibold py-2.5 shadow-sm hover:shadow-md hover:brightness-105 transition-all disabled:opacity-50"
        >
          {joining ? 'מצטרף…' : 'הצטרפות לתוכנית'}
          {!joining && (
            <span className="transition-transform group-hover/btn:-translate-x-0.5">{IconArrowLeft}</span>
          )}
        </button>
      </div>
    </div>
  );
};

const SchoolPrograms = () => {
  const { showAlert } = useUser();
  const {
    myPrograms,
    catalog,
    loading,
    fetchMyPrograms,
    fetchCatalog,
    joinProgram,
  } = useSchoolPrograms();

  const [view, setView] = useState(View.MINE);
  const [joiningId, setJoiningId] = useState(null);
  const [couponsModalProgram, setCouponsModalProgram] = useState(null);
  const [budgetModalProgram, setBudgetModalProgram] = useState(null);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [myProgramsQuery, setMyProgramsQuery] = useState('');

  useEffect(() => {
    fetchMyPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === View.CATALOG) fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const filteredCatalog = useMemo(
    () => catalog.filter((item) => matchesQuery(item, catalogQuery)),
    [catalog, catalogQuery]
  );

  const filteredMyPrograms = useMemo(
    () => myPrograms.filter((p) => matchesQuery(p, myProgramsQuery)),
    [myPrograms, myProgramsQuery]
  );

  const totals = useMemo(() => {
    return myPrograms.reduce(
      (acc, p) => ({
        allocated: acc.allocated + Number(p.allocatedBudget || 0),
        issued: acc.issued + Number(p.couponsIssuedAmount || 0),
        // "Scanned" — coupon value the student scanned into a wallet
        // for this program (still the couponsRedeemedAmount key on the
        // API for backward compatibility; couponsScannedAmount is the
        // same figure under a clearer name).
        scanned: acc.scanned + Number(p.couponsScannedAmount ?? p.couponsRedeemedAmount ?? 0),
        // "Used for lessons" — the further stage: that wallet balance
        // actually spent on a lesson purchase.
        usedForLessons: acc.usedForLessons + Number(p.couponsUsedForLessonsAmount || 0),
      }),
      { allocated: 0, issued: 0, scanned: 0, usedForLessons: 0 }
    );
  }, [myPrograms]);

  const handleJoin = async (programId) => {
    setJoiningId(programId);
    try {
      await joinProgram(programId);
      showAlert?.('בית הספר הצטרף לתוכנית בהצלחה', 'success');
    } catch (err) {
      showAlert?.(err.message || 'ההצטרפות לתוכנית נכשלה', 'error');
    } finally {
      setJoiningId(null);
    }
  };

  const isMine = view === View.MINE;
  const activeQuery = isMine ? myProgramsQuery : catalogQuery;
  const setActiveQuery = isMine ? setMyProgramsQuery : setCatalogQuery;

  return (
    <div className="md:min-h-screen bg-gray-100" dir="rtl">
      <SchoolProgramsHeader />

      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 pb-6 pt-2">
        {/* ── Filters — same structure as AdminConfirmDocuments' filter
            bar: p-4 sm:p-6 md:p-8 card, mb-6 below it, search first in
            the row as a plain bordered input (icon absolutely
            positioned inside), everything else auto-width beside it. ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3">
            {/* Search — full-width on xs, grows to fill on sm+ */}
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <input
                  value={activeQuery}
                  onChange={(e) => setActiveQuery(e.target.value)}
                  placeholder="חיפוש לפי שם, מספר תוכנית, נושא..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* View toggle — this page's equivalent of Admin's selects */}
            <div className="inline-flex overflow-hidden rounded-lg bg-gray-50 border border-gray-200 p-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => setView(View.MINE)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition whitespace-nowrap ${
                  view === View.MINE ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                התוכניות שלי ({myPrograms.length})
              </button>
              <button
                type="button"
                onClick={() => setView(View.CATALOG)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition whitespace-nowrap ${
                  view === View.CATALOG ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                קטלוג תוכניות
              </button>
            </div>

            <span className="inline-flex items-center gap-1.5 flex-shrink-0 rounded-full bg-indigo-50 border border-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-600">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
              {isMine ? `${filteredMyPrograms.length} תוכניות` : `${filteredCatalog.length} זמינות`}
            </span>
          </div>
        </div>

        {/* ── KPI Cards — literally the same markup pattern as
            AdminConfirmDocuments' counters (plain inline divs, not a
            shared component): 2 cols on xs, 4 on sm+, mb-6 below. ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 sm:p-4 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                {IconBudget}
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-indigo-700">תקציב מוקצה</p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-900">{formatMoney(totals.allocated)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3 sm:p-4 rounded-xl border border-amber-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                {IconIssued}
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-amber-700">קופונים שהונפקו</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-900">{formatMoney(totals.issued)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-sky-50 to-blue-50 p-3 sm:p-4 rounded-xl border border-sky-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                {IconRedeemed}
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-sky-700">קופונים שנסרקו</p>
                <p className="text-xl sm:text-2xl font-bold text-sky-900">{formatMoney(totals.scanned)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-3 sm:p-4 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                {IconUsedForLessons}
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-emerald-700">נוצלו לרכישת שיעורים</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-900">{formatMoney(totals.usedForLessons)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-1">
          {isMine ? (
            myPrograms.length === 0 ? (
              <EmptyState
                icon={IconTicketStack}
                title="בית הספר טרם הצטרף לאף תוכנית גפ״ן"
                body="עברו לקטלוג התוכניות כדי להצטרף לתוכנית הראשונה."
              />
            ) : filteredMyPrograms.length === 0 ? (
              <EmptyState icon={IconTicketStack} title="לא נמצאו תוכניות" body="נסו לשנות את מונח החיפוש." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                {filteredMyPrograms.map((program) => (
                  <SchoolProgramCard
                    key={program.id}
                    program={program}
                    onIssueCoupons={setCouponsModalProgram}
                    onPurchaseBudget={setBudgetModalProgram}
                  />
                ))}
              </div>
            )
          ) : catalog.length === 0 ? (
            <EmptyState icon={IconCatalog} title="אין כרגע תוכניות נוספות זמינות להצטרפות" />
          ) : filteredCatalog.length === 0 ? (
            <EmptyState icon={IconCatalog} title="לא נמצאו תוכניות" body="נסו לשנות את מונח החיפוש." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {filteredCatalog.map((item) => (
                <CatalogCard
                  key={item.id}
                  item={item}
                  onJoin={handleJoin}
                  joining={joiningId === item.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {couponsModalProgram && (
        <IssueCouponsModal
          program={couponsModalProgram}
          onClose={() => setCouponsModalProgram(null)}
        />
      )}

      {budgetModalProgram && (
        <PurchaseBudgetModal
          program={budgetModalProgram}
          onClose={() => setBudgetModalProgram(null)}
        />
      )}

      {loading && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}
    </div>
  );
};

export default SchoolPrograms;